import React, { createContext, useContext, useMemo, useState, useCallback } from 'react'
import { i18n } from '@fs/zion-locale'
import { searchChineseSurnames, fetchChineseSurnameById } from '../services/ChineseSurnamesAPI/chineseSurnamesAPI'
import useSurnameMapsAndColors from '../hooks/useSurnameMapsAndColors/useSurnameMapsAndColors'

const localizeNumber = (
  value,
  options = {
    capitalize: true,
    compactDisplay: 'short',
    maximumSignificantDigits: 3,
  }
) => {
  const languageCode = i18n.language
  let numberingSystem = languageCode === 'zh' ? 'hant' : 'hans'

  if (!languageCode?.startsWith('zh')) {
    numberingSystem = 'latn'
  }

  const config = {
    maximumFractionDigits: 2,
    notation: 'compact',
    numberingSystem,
    ...options,
  }

  let numberFormat
  try {
    numberFormat = new Intl.NumberFormat(languageCode === 'zh' ? 'zh-hant' : languageCode, config)
  } catch (err) {
    console.error('Invalid language:', languageCode, err)
    numberFormat = new Intl.NumberFormat('en', config)
  }

  const formattedValue = numberFormat.format(value)
  return options?.capitalize ? formattedValue.replace(/[a-z]/i, (match) => match.toUpperCase()) : formattedValue
}

export const ChineseSurnameContext = createContext()

export const useChineseSurnameInfo = () => {
  const context = useContext(ChineseSurnameContext)
  if (!context) {
    throw new Error('useChineseSurnameInfo must be used within a ChineseSurnameProvider')
  }
  return context
}

export const ChineseSurnameProvider = ({ children }) => {
  const [surnameSuggestions, setSurnameSuggestions] = useState([])
  const { fetchSurnameMaps, fetchNextSurnameColor, colorValuesMap } = useSurnameMapsAndColors()

  const searchSurnames = useCallback(async (searchText) => {
    if (!searchText || searchText.trim() === '') {
      setSurnameSuggestions([])
      return { success: true, noResults: false }
    }

    try {
      const result = await searchChineseSurnames(searchText, 5)

      if (result.success) {
        setSurnameSuggestions(result.data)
      } else {
        setSurnameSuggestions([])
      }

      return result
    } catch (error) {
      console.error(error)
      setSurnameSuggestions([])
      return { success: false, noResults: true, error }
    }
  }, [])

  const fetchSurnameData = useCallback(async (surnameId, userSurname = null) => {
    try {
      let surnameData = null

      if (surnameId) {
        surnameData = await fetchChineseSurnameById(surnameId)
      }

      if (!surnameData && userSurname) {
        const searchResult = await searchChineseSurnames(userSurname, 1)
        if (searchResult.success && searchResult.data?.length > 0) {
          const match = searchResult.data.find((item) => item.surname === userSurname) || searchResult.data[0]
          if (match?.id) {
            surnameData = await fetchChineseSurnameById(match.id)
          }
        }
      }

      if (surnameData) {
        return {
          id: surnameData.id,
          surname: surnameData.surname,
          romanization: surnameData.romanizations?.find(({ scheme }) => scheme === 'pinyin_tones')?.value || '',
          meaning: surnameData.meaning || '',
          population: localizeNumber(surnameData.population),
          rank: surnameData.rank || 0,
          hasData: true,
        }
      }
    } catch (error) {
      console.error('Error fetching surname data:', error)
    }

    return {
      surname: '',
      romanization: '',
      meaning: '',
      population: 0,
      rank: 0,
      hasData: false,
    }
  }, [])

  const value = useMemo(() => {
    return {
      surnameSuggestions,
      searchSurnames,
      fetchSurnameData,
      fetchSurnameMaps,
      fetchNextSurnameColor,
      colorValuesMap,
    }
  }, [surnameSuggestions, searchSurnames, fetchSurnameData, fetchSurnameMaps, fetchNextSurnameColor, colorValuesMap])

  return <ChineseSurnameContext.Provider value={value}>{children}</ChineseSurnameContext.Provider>
}
